import React, { useCallback, useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";
import { imageSrcSet, SIZES } from "@/lib/responsiveImage";

/**
 * The design's five-cell photo mosaic, with a lightbox behind it.
 *
 * It degrades down the number of photographs rather than leaving holes: one
 * photograph fills the frame, four fill the mosaic, more than four put a
 * "+N more" on the last cell. An agency that has not uploaded any gets no
 * gallery at all rather than five grey boxes.
 *
 * Alt text is the caption the agency wrote plus the street. `alt=""` on a
 * listing photograph tells a screen reader the picture is decorative, on a
 * page whose whole subject is the picture.
 */
/**
 * How many cells the mosaic has at this width.
 *
 * Five on desktop: three columns by two rows with the first spanning both.
 * Three on a phone, where the grid is two columns and a fourth photograph
 * spills into an implicit row the design never drew.
 *
 * Decided in JS rather than by hiding cells in CSS, because the "+N more"
 * badge has to count what is actually hidden. A CSS-hidden fourth cell
 * would leave the badge saying "+16" while seventeen were unseen.
 */
function useMosaicSize() {
  // FIVE on the first render, always, on both sides.
  //
  // This used to read matchMedia in the initialiser, which is fine for a
  // page React builds and fatal for one it hydrates: the server has no
  // matchMedia and renders five, a phone renders three, and React discards
  // the whole tree over two <div>s. Deterministic first, corrected in the
  // effect below a moment later.
  //
  // The correction cannot move anything: `.gallery` gives its implicit rows
  // no height, so the two cells a phone is about to drop were occupying
  // none. See foundationSites.css, which carries the same note.
  const [n, setN] = useState(5);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 900px)");
    const sync = () => setN(mq.matches ? 3 : 5);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return n;
}

// How far a finger has to travel across the photograph before it counts as
// "next one", and how far it may travel and still count as a tap. Between
// the two, nothing happens, which is the right answer for a hesitant drag.
const SWIPE_PX = 44;
const TAP_PX = 10;

export default function EstateGallery({ images = [], alt = "", tools = null }) {
  const [open, setOpen] = useState(-1);
  const size = useMosaicSize();
  const shown = images.slice(0, size);
  const extra = images.length - shown.length;

  // A swipe ends in a click event on the backdrop, which used to mean any
  // attempt to page through the photographs closed the lightbox instead.
  const touch = useRef(null);
  const swallowClick = useRef(false);

  const step = useCallback((delta) => {
    setOpen((i) => {
      if (i < 0) return i;
      const next = i + delta;
      return next < 0 || next > images.length - 1 ? i : next;
    });
  }, [images.length]);

  useEffect(() => {
    if (open < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(-1);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  // The next photograph, fetched while the current one is being looked at,
  // so paging on a phone is instant rather than a flash of empty backdrop.
  useEffect(() => {
    if (open < 0 || typeof Image === "undefined") return;
    [open + 1, open - 1].forEach((i) => {
      const img = images[i];
      if (!img) return;
      const pre = new Image();
      pre.src = mediaUrl(img.url);
    });
  }, [open, images]);

  if (!images.length) return null;

  const label = (img, i) => {
    const caption = String(img.caption || "").trim();
    return caption ? `${caption}, ${alt}` : `${alt}${i ? `, photograph ${i + 1}` : ""}`;
  };

  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    touch.current = t ? { x: t.clientX, y: t.clientY } : null;
  };

  const onTouchEnd = (e) => {
    const start = touch.current;
    touch.current = null;
    const t = e.changedTouches && e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Horizontal, and more horizontal than vertical — so a scroll-ish drag
    // down the screen does not shuffle the photographs sideways.
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
      step(dx < 0 ? 1 : -1);
      swallowClick.current = true;
    } else if (Math.abs(dx) > TAP_PX || Math.abs(dy) > TAP_PX) {
      swallowClick.current = true;
    }
  };

  const onBackdropClick = () => {
    if (swallowClick.current) { swallowClick.current = false; return; }
    setOpen(-1);
  };

  const arrow = (delta, glyph, name) => {
    const target = open + delta;
    const done = target < 0 || target > images.length - 1;
    return (
      <button type="button" className={`lightbox-step ${name}`} disabled={done}
              aria-label={delta > 0 ? "Next photograph" : "Previous photograph"}
              data-testid={`lightbox-${name}`}
              onClick={(e) => { e.stopPropagation(); step(delta); }}>
        <span aria-hidden="true">{glyph}</span>
      </button>
    );
  };

  return (
    <>
      {/* Fewer than three is a frame rather than a mosaic, and four leaves
          the same hole five fills: the grid's second row needs two. */}
      <div className={`gallery${shown.length < 3 ? " sparse" : shown.length === 4 ? " four" : ""}`}
           data-testid="estate-gallery" data-cells={shown.length}>
        {shown.map((img, i) => (
          <div key={img.id || i}>
            <button type="button" onClick={() => setOpen(i)}
                    aria-label={`Open ${label(img, i)} full size`}
                    style={{ display: "block", width: "100%", height: "100%", padding: 0 }}>
              <img src={mediaUrl(img.url)} alt={label(img, i)}
                   srcSet={imageSrcSet(img)} sizes={SIZES.gallery}
                   fetchPriority={i === 0 ? "high" : undefined}
                   loading={i === 0 ? "eager" : "lazy"} decoding="async" />
            </button>
            {i === 0 && tools && <div className="tools">{tools}</div>}
            {i === shown.length - 1 && extra > 0 && (
              <span className="tag dark more">+{extra} more</span>
            )}
          </div>
        ))}
      </div>

      {open >= 0 && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="Photographs"
             onClick={onBackdropClick} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
             data-testid="estate-lightbox">
          <button className="lightbox-close" type="button" aria-label="Close"
                  onClick={(e) => { e.stopPropagation(); setOpen(-1); }}>&times;</button>
          {images.length > 1 && arrow(-1, "‹", "prev")}
          {/* The one place the 2000px file is the right answer: the
              lightbox is as large as the screen allows, which is what it
              is for. `sizes` still lets a phone take a smaller rung. */}
          <img src={mediaUrl(images[open].url)} alt={label(images[open], open)}
               srcSet={imageSrcSet(images[open])} sizes={SIZES.full}
               onClick={(e) => e.stopPropagation()} />
          {images.length > 1 && arrow(1, "›", "next")}
          <div className="lightbox-count">{open + 1} of {images.length}</div>
        </div>
      )}
    </>
  );
}
